import { useCallback, useEffect, useState } from 'react';
import { api, ApiError } from '../api/client';
import type { Cupom, CuponsResponse } from '../api/types';
import { dateTime } from '../lib/format';
import { Banner, Spinner } from './States';

const brl = (v: number | null | undefined) =>
  v === null || v === undefined ? '—' : v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

/** Percentual como se escreve em português: 3,7%. */
const pct = (v: number) => `${v.toFixed(1).replace('.', ',')}%`;

/** Aceita "0,02" e "0.02" — ninguém deveria ter de lembrar qual o campo quer. */
const numero = (v: string) => Number(v.replace(',', '.'));

/** Data do input (YYYY-MM-DD) para o fim do dia, em ISO: cupom vence à meia-noite. */
function fimDoDia(data: string): string | null {
  if (!data) return null;
  const d = new Date(`${data}T23:59:59`);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

function inicioDoDia(data: string): string | null {
  if (!data) return null;
  const d = new Date(`${data}T00:00:00`);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

const soData = (iso: string | null) => (iso ? iso.slice(0, 10) : '');

/**
 * Cupons de desconto (parcerias com streamers).
 *
 * O desconto não é digitado em porcentagem: é uma cotação própria, como a do
 * kit. O painel mostra, ao lado, quanto isso representa em cada faixa de valor
 * — é a pergunta que o streamer faz ("quanto de desconto meu cupom dá?") e a
 * resposta honesta depende da faixa.
 */
export function CuponsPanel() {
  const [dados, setDados] = useState<CuponsResponse | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);

  // Cupom novo, antes de salvar.
  const [code, setCode] = useState('');
  const [streamer, setStreamer] = useState('');
  const [bonus, setBonus] = useState('0,02');
  const [inicio, setInicio] = useState('');
  const [fim, setFim] = useState('');
  const [maxUsos, setMaxUsos] = useState('');
  const [umPorCliente, setUmPorCliente] = useState(true);
  const [minimo, setMinimo] = useState('');

  const carregar = useCallback(async () => {
    try {
      setDados(await api.adminCupons());
      setErro(null);
    } catch (e) {
      setErro(e instanceof ApiError ? e.message : 'Não consegui carregar os cupons.');
    }
  }, []);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  async function agir(acao: () => Promise<unknown>, mensagem?: string) {
    setOcupado(true);
    setErro(null);
    setOk(null);
    try {
      await acao();
      if (mensagem) setOk(mensagem);
      await carregar();
    } catch (e) {
      setErro(e instanceof ApiError ? e.message : 'Não consegui salvar.');
    } finally {
      setOcupado(false);
    }
  }

  function criar() {
    const rateBonus = numero(bonus);
    if (!code.trim() || !Number.isFinite(rateBonus)) {
      setErro('Informe o código e o acréscimo do cupom.');
      return;
    }
    void agir(
      () =>
        api.adminCriarCupom({
          code: code.trim(),
          streamer: streamer.trim() || null,
          rateBonus,
          startsAt: inicioDoDia(inicio),
          endsAt: fimDoDia(fim),
          maxUses: maxUsos.trim() ? Number(maxUsos) : null,
          onePerClient: umPorCliente,
          minTotalBrl: minimo.trim() ? numero(minimo) : null,
        }),
      `Cupom ${code.trim().toUpperCase()} criado.`,
    ).then(() => {
      setCode('');
      setStreamer('');
      setMaxUsos('');
      setMinimo('');
      setInicio('');
      setFim('');
    });
  }

  if (erro && !dados) return <Banner kind="error">{erro}</Banner>;
  if (!dados) return <Spinner label="Carregando os cupons…" />;

  const faixas = dados.faixas.filter((f) => f.active !== false);

  /**
   * Quanto o cupom desconta numa faixa.
   *
   * O preço é custo × taxa × (cotação + acréscimo) / cotação. Trocando o
   * acréscimo da faixa pelo do cupom, a diferença relativa é esta — é o número
   * que o streamer divulga.
   */
  const descontoNaFaixa = (bonusCupom: number, bonusFaixa: number, cotacao = 0.78) => {
    const antes = cotacao + bonusFaixa;
    const depois = cotacao + bonusCupom;
    if (antes <= 0 || depois >= antes) return 0;
    return ((antes - depois) / antes) * 100;
  };

  return (
    <div className="panel">
      <h2>Cupons</h2>
      <p className="muted">
        Parcerias com streamers. O cupom não é um percentual solto: ele dá uma <b>cotação própria</b>, como o kit. O
        desconto sai maior nas faixas de valor em que a loja ganha mais, e some onde a margem já é pequena — assim
        nenhum pedido sai no prejuízo.
      </p>

      {erro && <Banner kind="error">{erro}</Banner>}
      {ok && <Banner kind="ok">{ok}</Banner>}

      {dados.items.length === 0 ? (
        <p className="muted">Nenhum cupom ainda. Crie o primeiro abaixo.</p>
      ) : (
        <div className="table-wrap">
          <table className="admin-table cards-no-mobile">
            <thead>
              <tr>
                <th>Cupom</th>
                <th>Acréscimo</th>
                <th>Desconto por faixa</th>
                <th>Regras</th>
                <th>Usos</th>
                <th>Rendeu</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {dados.items.map((c) => (
                <tr key={c.id} className={c.active ? '' : 'indisponivel'}>
                  <td data-label="Cupom">
                    <b>{c.code}</b>
                    {c.streamer && <div className="dim">{c.streamer}</div>}
                    {c.ultimoUso && <div className="dim cupom-quando">último uso {dateTime(c.ultimoUso)}</div>}
                  </td>
                  <td data-label="Acréscimo" className="dim">
                    {c.rateBonus.toFixed(4).replace('.', ',')}
                  </td>
                  <td data-label="Desconto por faixa">
                    <span className="cupom-faixas">
                      {faixas.map((f) => (
                        <span key={`${c.id}-${f.minBrl}`}>
                          {brl(f.minBrl)}
                          {f.maxBrl === null ? '+' : ` a ${brl(f.maxBrl)}`}:{' '}
                          <b>{pct(descontoNaFaixa(c.rateBonus, f.rateBonus))}</b>
                        </span>
                      ))}
                    </span>
                  </td>
                  <td data-label="Regras" className="dim">
                    <Regras cupom={c} />
                  </td>
                  <td data-label="Usos">
                    <b>{c.usos}</b>
                    {c.maxUses !== null && <span className="dim"> / {c.maxUses}</span>}
                  </td>
                  <td data-label="Rendeu">
                    {brl(c.vendidoBrl)}
                    <div className="dim">desconto {brl(c.descontoBrl)}</div>
                  </td>
                  <td>
                    <div className="actions">
                      <button
                        className="btn ghost small"
                        disabled={ocupado}
                        onClick={() => void agir(() => api.adminAtualizarCupom(c.id, { active: !c.active }))}
                        title={c.active ? 'Parar de aceitar este cupom' : 'Voltar a aceitar'}
                      >
                        {c.active ? 'Desligar' : 'Ligar'}
                      </button>
                      <button
                        className="btn ghost small"
                        disabled={ocupado}
                        onClick={() => {
                          if (!window.confirm(`Remover o cupom ${c.code}? O histórico de usos vai junto.`)) return;
                          void agir(() => api.adminRemoverCupom(c.id), `Cupom ${c.code} removido.`);
                        }}
                      >
                        Remover
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <h3>Criar cupom</h3>
      <div className="cupom-form">
        <label className="field">
          <span>Código</span>
          <input
            className="input"
            placeholder="STREAMERX"
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            disabled={ocupado}
          />
        </label>
        <label className="field">
          <span>Streamer (opcional)</span>
          <input
            className="input"
            placeholder="Nome do parceiro"
            value={streamer}
            onChange={(e) => setStreamer(e.target.value)}
            disabled={ocupado}
          />
        </label>
        <label className="field">
          <span>Acréscimo do cupom</span>
          <input className="input" value={bonus} onChange={(e) => setBonus(e.target.value)} disabled={ocupado} />
          <small className="dim">
            Menor que o da faixa = desconto. As faixas de hoje:{' '}
            {faixas.map((f) => f.rateBonus.toFixed(2).replace('.', ',')).join(' · ')}
          </small>
        </label>
        <label className="field">
          <span>Começa em (opcional)</span>
          <input
            className="input"
            type="date"
            value={inicio}
            onChange={(e) => setInicio(e.target.value)}
            disabled={ocupado}
          />
        </label>
        <label className="field">
          <span>Vence em (opcional)</span>
          <input className="input" type="date" value={fim} onChange={(e) => setFim(e.target.value)} disabled={ocupado} />
        </label>
        <label className="field">
          <span>Limite de usos (opcional)</span>
          <input
            className="input"
            inputMode="numeric"
            placeholder="sem limite"
            value={maxUsos}
            onChange={(e) => setMaxUsos(e.target.value.replace(/\D/g, ''))}
            disabled={ocupado}
          />
        </label>
        <label className="field">
          <span>Valor mínimo do pedido (opcional)</span>
          <input
            className="input"
            inputMode="decimal"
            placeholder="sem mínimo"
            value={minimo}
            onChange={(e) => setMinimo(e.target.value)}
            disabled={ocupado}
          />
        </label>
        <label className="field cupom-check">
          <input
            type="checkbox"
            checked={umPorCliente}
            onChange={(e) => setUmPorCliente(e.target.checked)}
            disabled={ocupado}
          />
          <span>Uma vez por cliente</span>
        </label>
      </div>

      <div className="actions-row">
        <button className="btn primary" onClick={criar} disabled={ocupado || !code.trim()}>
          Criar cupom
        </button>
      </div>

      {/* Prévia: o número que o streamer vai divulgar, antes de o cupom existir. */}
      {Number.isFinite(numero(bonus)) && faixas.length > 0 && (
        <p className="muted cupom-previa">
          Com esse acréscimo, o desconto fica em{' '}
          {faixas.map((f, i) => (
            <span key={f.minBrl}>
              {i > 0 ? ' · ' : ''}
              <b>{pct(descontoNaFaixa(numero(bonus), f.rateBonus))}</b> de {brl(f.minBrl)}
              {f.maxBrl === null ? ' pra cima' : ` a ${brl(f.maxBrl)}`}
            </span>
          ))}
          .
        </p>
      )}
    </div>
  );
}

function Regras({ cupom }: { cupom: Cupom }) {
  const partes: string[] = [];
  if (cupom.onePerClient) partes.push('1 por cliente');
  if (cupom.minTotalBrl !== null) partes.push(`mínimo ${brl(cupom.minTotalBrl)}`);
  if (cupom.startsAt) partes.push(`a partir de ${soData(cupom.startsAt).split('-').reverse().join('/')}`);
  if (cupom.endsAt) partes.push(`até ${soData(cupom.endsAt).split('-').reverse().join('/')}`);
  if (!cupom.active) partes.push('desligado');
  return <>{partes.length > 0 ? partes.join(' · ') : 'sem limites'}</>;
}
